import { useId } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Divider } from "#components/atoms/Divider/Divider";
import { Switch } from "#components/atoms/Switch/Switch";
import { Text } from "#components/atoms/Typographies/Text";
import { formatGain } from "#modules/optimizations/domain/formatGain";
import { getOptimizationGain } from "#modules/optimizations/domain/getOptimizationGain";
import {
  getOptimizationSubtitle,
  getOptimizationTitle,
} from "#modules/optimizations/domain/optimizationCardContent";
import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import { CompatibleLinesAlert } from "#modules/optimizations/view/CompatibleLinesAlert";
import { DeletedLinesAlert } from "#modules/optimizations/view/DeletedLinesAlert";
import { useRestoreOptimization } from "#modules/optimizations/view/useRestoreOptimization";
import { Box } from "#shared/view/components/Box";
import { Center } from "#shared/view/components/Center";
import { Flex } from "#shared/view/components/Flex";
import { Paper } from "#shared/view/components/Paper";
import { Stack } from "#shared/view/components/Stack";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";

const MAX_VISIBLE_PARTS = 8;
const THUMBNAIL_SIZE = 48;

type Props = {
  optimization: OptimizationDto;
  onOpenDetail?: () => void;
  onToggle?: (enabled: boolean) => void;
  isTogglePending?: boolean;
  isLineIgnored?: (lineId: string) => boolean;
  onIgnoreLine?: (lineId: string) => void;
};

export const OptimizationCard = ({
  optimization,
  onOpenDetail,
  onToggle,
  isTogglePending = false,
  isLineIgnored,
  onIgnoreLine,
}: Props) => {
  const { t } = useTranslation();
  const applyToQuoteId = useId();
  const { restoreOptimization, isPending: isRestorePending } = useRestoreOptimization({
    quoteRequestId: optimization.quoteRequestId,
  });
  const openDetail = () => {
    if (!optimization.deletedAt) {
      onOpenDetail?.();
      return;
    }

    restoreOptimization({ optimizationId: optimization.id }, { onSuccess: () => onOpenDetail?.() });
  };
  const { gain, isCalculated, hasGain } = getOptimizationGain(optimization);
  const title = getOptimizationTitle(optimization, t);
  const subtitle = getOptimizationSubtitle(optimization, t);
  const hiddenPartCount = optimization.quoteRequestLines.length - MAX_VISIBLE_PARTS;
  const visibleCompatibleLines = optimization.compatibleLines.filter(
    (line) => !isLineIgnored?.(line.id),
  );
  const deletedNotifications = optimization.optimizationNotifications.filter(
    (notification) => notification.readAt === null,
  );
  const hasAlerts = visibleCompatibleLines.length > 0 || deletedNotifications.length > 0;

  const card = (
    <Box
      bg={colors.bg.elevation.primary}
      p={spacing[6]}
      style={{
        border: `1px solid ${colors.border.tertiary}`,
        borderRadius: radius.xl,
      }}
    >
      <Stack gap={spacing[4]}>
        <Flex align="flex-start" gap={spacing[4]} justify="space-between">
          <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
            <Text size="base" type="medium">
              {title}
            </Text>
            {subtitle ? (
              <Text size="small" color={colors.text.secondaryDefault} truncate>
                {subtitle}
              </Text>
            ) : null}
          </Stack>
          <Box style={{ flexShrink: 0 }}>
            <Button
              variant="secondary"
              size="sm"
              loading={Boolean(optimization.deletedAt) && isRestorePending}
              onClick={openDetail}
            >
              {isCalculated
                ? t("optimizations.card.viewDetail")
                : t("optimizations.card.calculateGain")}
            </Button>
          </Box>
        </Flex>
        <Flex gap={spacing[2]}>
          {optimization.quoteRequestLines.slice(0, MAX_VISIBLE_PARTS).map((line) => (
            <ThumbnailOverlay
              key={line.id}
              part={line.part}
              size={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
            />
          ))}
          {hiddenPartCount > 0 ? (
            <Paper
              h={THUMBNAIL_SIZE}
              w={THUMBNAIL_SIZE}
              radius="sm"
              withBorder
              bg={colors.bg.elevation.quaternary}
            >
              <Center h="100%" w="100%">
                <Text size="small" type="medium">
                  +{hiddenPartCount}
                </Text>
              </Center>
            </Paper>
          ) : null}
        </Flex>
        {isCalculated ? (
          <>
            <Divider type="secondary" />
            <Flex align="center" justify="space-between" gap={spacing[4]}>
              <Text size="base" type="medium" color={colors.text.success}>
                {formatGain(gain)}
              </Text>
              <Flex align="center" gap={spacing[2]}>
                <label htmlFor={applyToQuoteId} style={{ cursor: "pointer" }}>
                  <Text size="tiny" color={colors.text.secondaryDefault} component="span">
                    {t("optimizations.detail.applyToQuote")}
                  </Text>
                </label>
                <Switch
                  id={applyToQuoteId}
                  checked={optimization.enabled && hasGain}
                  disabled={!hasGain || isTogglePending}
                  onChange={(enabled) => onToggle?.(enabled)}
                />
              </Flex>
            </Flex>
          </>
        ) : null}
      </Stack>
    </Box>
  );

  if (!hasAlerts) {
    return card;
  }

  return (
    <Box bg={colors.bg.elevation.tertiary} style={{ borderRadius: radius.xl }}>
      {card}
      {visibleCompatibleLines.length > 0 ? (
        <CompatibleLinesAlert
          lines={visibleCompatibleLines}
          optimizationId={optimization.id}
          quoteRequestId={optimization.quoteRequestId}
          onIgnore={(lineId) => onIgnoreLine?.(lineId)}
        />
      ) : null}
      {deletedNotifications.length > 0 ? (
        <DeletedLinesAlert
          notifications={deletedNotifications}
          optimizationId={optimization.id}
          quoteRequestId={optimization.quoteRequestId}
        />
      ) : null}
    </Box>
  );
};
